import { useEffect, useState, useRef } from "react";
import { API } from "@/lib/api";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sparkles, Send } from "lucide-react";

const PRESETS = [
  "Que dois-je faire cette semaine ?",
  "Quel est l'état de mon budget ?",
  "Quels invités n'ont pas encore répondu ?",
  "Prépare un planning pour le jour J.",
  "Quels paiements arrivent dans les 30 prochains jours ?",
];

export default function AiAssistant() {
  const { wedding } = useAuth();
  const wid = wedding?.wedding?.id;
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    if (wid) api.get(`/weddings/${wid}/ai/conversations`).then(({ data }) =>
      setMessages(data.map((m) => ({ role: m.role, text: m.text }))));
  }, [wid]);
  useEffect(() => { endRef.current?.scrollIntoView(); }, [messages]);

  const send = async (msg) => {
    const text = (msg || input).trim();
    if (!text || streaming) return;
    setInput(""); setStreaming(true);
    setMessages((m) => [...m, { role: "user", text }, { role: "assistant", text: "" }]);
    try {
      const token = localStorage.getItem("token");
      const resp = await fetch(`${API}/weddings/${wid}/ai/chat`, {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message: text }),
      });
      if (!resp.ok) {
        const e = await resp.json().catch(() => ({}));
        setMessages((m) => { const c = [...m]; c[c.length - 1].text = (e.detail?.message || e.detail || "L'assistant est indisponible."); return c; });
        setStreaming(false); return;
      }
      const reader = resp.body.getReader();
      const dec = new TextDecoder();
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += dec.decode(value, { stream: true });
        setMessages((m) => { const c = [...m]; c[c.length - 1].text = acc; return c; });
      }
    } catch {
      setMessages((m) => { const c = [...m]; c[c.length - 1].text = "Erreur de connexion."; return c; });
    } finally { setStreaming(false); }
  };

  return (
    <div>
      <PageHeader eyebrow="Assistant IA" title="Votre concierge de mariage"
        description="Il connaît votre budget, vos invités et votre planning. Posez-lui vos questions." />
      <div className="card-lux flex flex-col h-[calc(100vh-260px)]">
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {messages.length === 0 && (
            <div className="h-full flex flex-col items-center justify-center text-center">
              <div className="w-14 h-14 rounded-2xl bg-stone-900 flex items-center justify-center mb-4">
                <Sparkles className="w-6 h-6 text-champagne" /></div>
              <h3 className="font-serif text-xl">Comment puis-je vous aider ?</h3>
              <div className="flex flex-wrap gap-2 justify-center mt-5 max-w-lg">
                {PRESETS.map((p) => (
                  <button key={p} onClick={() => send(p)} data-testid="ai-preset"
                    className="px-3 py-1.5 rounded-full text-sm border bg-white hover:bg-accent text-stone-600">{p}</button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-2xl px-4 py-3 whitespace-pre-wrap text-sm ${
                m.role === "user" ? "bg-stone-900 text-white" : "bg-accent text-stone-800"}`}>
                {m.text || <span className="opacity-50">…</span>}</div>
            </div>
          ))}
          <div ref={endRef} />
        </div>
        <div className="border-t p-3 flex gap-2">
          <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="Posez votre question…" className="h-11" data-testid="ai-input" disabled={streaming} />
          <Button onClick={() => send()} disabled={streaming} className="rounded-full h-11 px-5" data-testid="ai-send"><Send className="w-4 h-4" /></Button>
        </div>
      </div>
    </div>
  );
}
